import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/hooks/usePageTitle";
import AccountNavMenu from "@/components/AccountNavMenu";

// Section card primitive - mirrors RulesPage for visual consistency.
function Section({ title, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-4 sm:p-6 mb-4"
    >
      <h2
        className="text-base sm:text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-3"
        style={{ fontFamily: "Fredoka" }}
      >
        {title}
      </h2>
      <div className="text-sm text-[#1A237E]/80 dark:text-[#E3F2FD]/80 space-y-2 leading-relaxed break-words">
        {children}
      </div>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-2 items-start">
      <span className="shrink-0 text-[#38B6FF] font-bold leading-relaxed">•</span>
      <span className="min-w-0 break-words">{children}</span>
    </li>
  );
}

// Template content - clearly marked for legal review before launch.
export default function TermsPage() {
  const { t } = useTranslation();
  usePageTitle("Terms of Service");
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] p-4 md:p-8"
      id="main-content"
      role="main"
      tabIndex={-1}
      data-testid="terms-page"
    >
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-3">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard"
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("common.back")}
          </Button>
          <AccountNavMenu />
        </div>
        <h1
          className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-2 mb-2 break-words"
          style={{ fontFamily: "Fredoka" }}
        >
          <Scale className="w-6 h-6 text-[#FFC107] shrink-0" />
          {t("footer.terms")}
        </h1>
        <p className="text-xs sm:text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-6 italic">
          Last updated: this is a template. Have counsel review before launch.
        </p>

        <Section title="Acceptance of These Terms" testId="terms-acceptance">
          <p>
            These Terms of Service ("Terms") govern your access to and use
            of Market Melee ("the Game"). By creating an account or playing,
            you agree to these Terms. If you do not agree, do not use the
            Game. This document is a template and must be reviewed by
            qualified legal counsel before it is relied upon.
          </p>
        </Section>

        <Section title="Not Real Cryptocurrency or Gambling" testId="terms-nonfinancial">
          <p>
            Market Melee is a free-to-play, crypto-<em>themed</em> game of
            skill and strategy. All coins, prices, balances, and portfolios
            are <strong>fictional and have no real-world value</strong>. The
            Game does not involve real cryptocurrency, real money, deposits,
            withdrawals, wagering, or gambling, and is not financial,
            investment, or trading advice.
          </p>
        </Section>

        <Section title="Eligibility and Accounts" testId="terms-accounts">
          <ul className="space-y-1 pl-1">
            <Bullet>
              You must meet the minimum age required by your local law to
              form a binding agreement and to use online services.
            </Bullet>
            <Bullet>
              You are responsible for the accuracy of your account
              information and for keeping your credentials secure.
            </Bullet>
            <Bullet>
              You are responsible for all activity that occurs under your
              account.
            </Bullet>
          </ul>
        </Section>

        <Section title="Acceptable Use" testId="terms-acceptable-use">
          <p>You agree not to:</p>
          <ul className="space-y-1 pl-1">
            <Bullet>
              Cheat, exploit bugs, use bots, or otherwise gain an unfair
              advantage.
            </Bullet>
            <Bullet>
              Harass, abuse, or harm other players, or post unlawful or
              offensive content.
            </Bullet>
            <Bullet>
              Attempt to disrupt, reverse engineer, overload, or gain
              unauthorized access to the Game or its systems.
            </Bullet>
            <Bullet>
              Use the Game for any unlawful purpose or in violation of these
              Terms.
            </Bullet>
          </ul>
        </Section>

        <Section title="Your Content and Conduct" testId="terms-content">
          <p>
            You are responsible for any display name, messages, or other
            content you submit. We may remove content or restrict accounts
            that violate these Terms or harm the community or the Game.
          </p>
        </Section>

        <Section title="Intellectual Property" testId="terms-ip">
          <p>
            The Game, including its design, artwork, code, and content, is
            owned by us or our licensors and is protected by applicable
            laws. We grant you a limited, personal, non-transferable,
            revocable license to use the Game for personal, non-commercial
            entertainment, subject to these Terms.
          </p>
        </Section>

        <Section title="Service Availability and Changes" testId="terms-availability">
          <p>
            We may modify, suspend, or discontinue any part of the Game at
            any time, and we may update gameplay, features, or these Terms.
            We do not guarantee uninterrupted or error-free availability.
          </p>
        </Section>

        <Section title="Disclaimers" testId="terms-disclaimers">
          <p>
            The Game is provided "as is" and "as available", without
            warranties of any kind to the fullest extent permitted by law.
            We do not warrant that the Game will be uninterrupted, secure,
            or free of errors.
          </p>
        </Section>

        <Section title="Limitation of Liability" testId="terms-liability">
          <p>
            To the maximum extent permitted by law, we will not be liable
            for any indirect, incidental, special, consequential, or
            punitive damages arising from your use of the Game. This section
            should be tailored to your jurisdiction with counsel.
          </p>
        </Section>

        <Section title="Termination" testId="terms-termination">
          <p>
            We may suspend or terminate your access to the Game if you
            violate these Terms or to protect the Game and its players. You
            may stop using the Game and request account deletion at any
            time.
          </p>
        </Section>

        <Section title="Changes to These Terms" testId="terms-changes">
          <p>
            We may update these Terms from time to time. When we do, we will
            revise the "Last updated" date above and, where appropriate,
            provide additional notice. Continued use after changes take
            effect means you accept the updated Terms.
          </p>
        </Section>

        <Section title="Contact Us" testId="terms-contact">
          <p>
            Questions about these Terms? Contact us through the support
            channel listed in the Game. (Replace with your real contact
            details before launch.)
          </p>
        </Section>
      </div>
    </div>
  );
}
